export type MedcallMedicationConfirmationDraft = {
  administrationNotes: string;
  customDays: string[];
  dateDue: string;
  dateGiven: string;
  dateInjectionArrived: string;
  frequency: string;
  importanceLevel: string;
  medicineName: string;
  onSite: string;
  prn: boolean;
  refillDate: string;
  refillStatus: boolean;
  route: string;
  startDate: string;
  stopDate: string;
  stopDateEnabled: boolean;
  strength: string;
  timesPerDay: string;
};

export type MedcallMedicationConfirmationRow = {
  label: string;
  value: string;
};

const formatLegacyDate = (value: string) => {
  const match = `${value || ''}`.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return '';
  }

  return `${match[2]}/${match[3]}/${match[1].slice(-2)}`;
};

const formatLegacyDateTime = (value: string) => {
  const match = `${value || ''}`.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) {
    return '';
  }

  const hour = Number(match[4]);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${match[2]}/${match[3]}/${match[1].slice(-2)} ${`${displayHour}`.padStart(2, '0')}:${match[5]} ${suffix}`;
};

const getLegacyFrequency = (draft: MedcallMedicationConfirmationDraft) => {
  if (draft.frequency === 'everyotherday') {
    return 'QOD';
  }

  if (draft.frequency === 'everyday') {
    return draft.timesPerDay.toUpperCase();
  }

  if (draft.frequency === 'custom_days') {
    return `CUSTOM WEEK DAYS: ${draft.customDays.join(', ')} - ${draft.timesPerDay}`.toUpperCase();
  }

  return draft.frequency.toUpperCase();
};

const getLegacyInjectionStatus = (onSite: string) => {
  if (onSite === '0') {
    return 'Not Yet On-Site';
  }

  if (onSite === '1') {
    return 'N.A.';
  }

  return 'On Site';
};

export const buildLegacyMedcallMedicationConfirmationRows = (
  draft: MedcallMedicationConfirmationDraft,
) => {
  const rows: MedcallMedicationConfirmationRow[] = [
    { label: 'Medicine Name', value: draft.medicineName },
    { label: 'Strength', value: draft.strength },
    { label: 'Route', value: draft.route },
    {
      label: 'Importance Level',
      value: draft.route === 'IM-LAI' ? '2' : draft.importanceLevel,
    },
    { label: 'Frequency', value: getLegacyFrequency(draft) },
  ];

  if (draft.route !== 'IM-LAI') {
    rows.push({ label: 'PRN', value: draft.prn ? 'Yes' : 'No' });
    rows.push({ label: 'Start Date/Time', value: formatLegacyDateTime(draft.startDate) });
    if (draft.stopDateEnabled) {
      rows.push({
        label: 'Discontinue Date/Time',
        value: formatLegacyDateTime(draft.stopDate),
      });
    }
    if (draft.refillStatus) {
      rows.push({ label: 'Runs Out Date', value: formatLegacyDate(draft.refillDate) });
    }
  }

  if (draft.route === 'IM-LAI') {
    rows.push({ label: 'Date Due', value: formatLegacyDate(draft.dateDue) });
    rows.push({ label: 'Injection Status', value: getLegacyInjectionStatus(draft.onSite) });
    if (draft.onSite === '2') {
      rows.push({
        label: 'Date Injection Arrived',
        value: formatLegacyDate(draft.dateInjectionArrived),
      });
    }
    rows.push({ label: 'Date Given', value: formatLegacyDate(draft.dateGiven) });
  }

  rows.push({ label: 'Injection site location', value: draft.administrationNotes });
  return rows;
};
